import { TabPanel as ChakraTabPanel } from '@chakra-ui/react'
import * as React from 'react'

import { isDefined } from '~logic/helpers/isDefined'
import { Grid } from '~ui/components/layout/Box'
import { AspectRatio } from '~ui/components/media/AspectRatio/AspectRatio'
import { Image } from '~ui/components/media/Image/Image'
import { H2 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'
import { letterSpacingValues } from '~ui/styleGuide/fontStyles'
import { clipPaths } from '~ui/styleGuide/styles'
import { parseMarkdown } from '~ui/utils/parseMarkdown'

type TTextProps = Pick<PropsOf<typeof Text>, 'textAlign' | 'contrast'>

type TItem = {
  /**
   * header of the tab
   */
  header?: Nullable<string>
  /**
   * image of the tab
   */
  image?: Nullable<string>
  /**
   * content of the tab
   */
  text: string
}

type TProps = NoChildren & {
  /**
   * data to be rendered
   */
  item: TItem
  /**
   * is the carousel rendered on mobile device
   */
  isMobile: boolean
  /**
   * any image element such as next/image, HTML img element or other, if no such element is provided, the chakra image is used with the item's imageUrl
   */
  customImageElement?: React.ReactNode
}

/**
 * Content of the Carousel tab.
 * Renders an image and a heading with a description.
 * If customImageElement is provided, it is rendered instead of the default Chakra Image.
 */
export const CarouselTabContent: React.FC<TProps> = ({ item, isMobile, customImageElement }) => {
  const hasHeader = isDefined(item.header)

  const textProps: TTextProps = {
    textAlign: `center`,
    contrast: `low`,
  }

  const hasImage = customImageElement || item.image

  /**
   * @returns provided customImageElement (such as next/image) OR Chakra Image with item's imageUrl OR null if both customImageElement and item.imageUrl are undefined
   */
  const renderImageElement = () => {
    if (customImageElement) {
      return customImageElement
    }

    if (item.image) {
      return (
        <Image
          clipPath={clipPaths.bottomEllipse}
          src={item.image}
          alt={`${item.header} image`}
          objectFit="cover"
        />
      )
    }

    return null
  }

  return (
    <ChakraTabPanel padding={0}>
      {/* Image cropped at the bottom with ellipse */}
      {hasImage && (
        <AspectRatio ratio={isMobile ? `threeToTwo` : `threeToOne`}>
          {renderImageElement()}
        </AspectRatio>
      )}

      {/* Heading & description */}
      <Grid
        gap={`smallMedium`}
        justifyItems={`center`}
        alignContent={`baseline`}
        padding={{
          left: `mediumLarge`,
          right: `mediumLarge`,
          bottom: `none`,
          top: isMobile ? `largePlus` : `extraLarge`,
        }}
      >
        {hasHeader && (
          <H2
            lineHeight={`1.2`}
            fontSize={`l24`}
            fontStyle={`bold`}
            letterSpacing={letterSpacingValues.tighter}
          >
            {item.header}
          </H2>
        )}
        <Text {...textProps} dangerouslySetInnerHTML={{ __html: parseMarkdown(item.text) }} />
      </Grid>
    </ChakraTabPanel>
  )
}
